import React from "react";
import { MessageSquare, Lightbulb, AlertTriangle } from "lucide-react";

/**
 * FOH Guidance Panel — calm "senior advisor whisper" layer that sits in the
 * right rail of every Companion screen (MOT / Service / Diagnostic).
 *
 * Mirrors the tonal language of the Booking Companion + Call Companion modal
 * so the FOH sees a consistent coaching voice across every intake screen.
 *
 *   Props
 *     tone        — "teal" | "amber" | "rose"  (default: teal)
 *     title       — short header label (e.g. "MOT guidance")
 *     askNext     — string[] of questions to ask next
 *     sayToCustomer — string[] of verbatim phrases to read out
 *     watchOuts   — string[] of risks / cautions
 *
 * Renders nothing when there is no content — stays supportive, not noisy.
 */
export default function FOHGuidancePanel({
  tone = "teal",
  title = "FOH guidance",
  askNext = [],
  sayToCustomer = [],
  watchOuts = [],
}) {
  const empty =
    askNext.length === 0 && sayToCustomer.length === 0 && watchOuts.length === 0;
  if (empty) return null;

  const accent = ACCENT[tone] || ACCENT.teal;

  return (
    <aside
      className={`rounded-lg border bg-white overflow-hidden ${accent.border}`}
      data-testid="foh-guidance-panel"
    >
      <header
        className={`px-3.5 pt-2.5 pb-2 flex items-center gap-1.5 border-b ${accent.headerBorder} ${accent.headerBg}`}
      >
        <div className={`w-1.5 h-1.5 rounded-full ${accent.dot}`} />
        <p
          className={`text-[12px] uppercase tracking-[0.18em] font-semibold ${accent.headerText}`}
        >
          {title}
        </p>
      </header>

      <div className="px-3.5 py-3.5 space-y-4">
        {askNext.length > 0 && (
          <Section
            icon={<MessageSquare className="w-4 h-4" />}
            label="Ask next"
            tone="slate"
          >
            <ul className="space-y-2.5">
              {askNext.map((g, i) => (
                <li
                  key={i}
                  className="text-[15px] text-slate-800 leading-relaxed"
                >
                  {g}
                </li>
              ))}
            </ul>
          </Section>
        )}

        {sayToCustomer.length > 0 && (
          <Section
            icon={<Lightbulb className="w-4 h-4" />}
            label="Say to the customer"
            tone="amber"
          >
            <ul className="space-y-2.5">
              {sayToCustomer.map((p, i) => (
                <li
                  key={i}
                  className="text-[15px] text-amber-900 leading-relaxed italic pl-3 border-l-2 border-amber-300"
                >
                  &ldquo;{p}&rdquo;
                </li>
              ))}
            </ul>
          </Section>
        )}

        {watchOuts.length > 0 && (
          <Section
            icon={<AlertTriangle className="w-4 h-4" />}
            label="Watch-outs"
            tone="rose"
          >
            <ul className="space-y-2">
              {watchOuts.map((w, i) => (
                <li
                  key={i}
                  className="text-[14px] text-rose-900 leading-relaxed"
                >
                  {w}
                </li>
              ))}
            </ul>
          </Section>
        )}
      </div>
    </aside>
  );
}

function Section({ icon, label, tone = "slate", children }) {
  const labelTone = LABEL_TONE[tone] || LABEL_TONE.slate;
  return (
    <section>
      <div
        className={`flex items-center gap-1.5 text-[11.5px] uppercase tracking-[0.18em] font-semibold mb-2 ${labelTone}`}
      >
        {icon}
        {label}
      </div>
      {children}
    </section>
  );
}

const ACCENT = {
  teal: {
    border: "border-teal-200",
    headerBorder: "border-teal-100",
    headerBg: "bg-teal-50/60",
    headerText: "text-teal-800",
    dot: "bg-teal-500",
  },
  amber: {
    border: "border-amber-200",
    headerBorder: "border-amber-100",
    headerBg: "bg-amber-50/60",
    headerText: "text-amber-800",
    dot: "bg-amber-500",
  },
  rose: {
    border: "border-rose-200",
    headerBorder: "border-rose-100",
    headerBg: "bg-rose-50/60",
    headerText: "text-rose-800",
    dot: "bg-rose-500",
  },
};

const LABEL_TONE = {
  slate: "text-slate-600",
  amber: "text-amber-700",
  rose: "text-rose-700",
};
